<template>
    <Popover placement="bottom-center">
        <template #trigger>
            <div class="flex-1 bg-custom-gray-100 rounded-lg border border-custom-gray-100 cursor-pointer focus-border" tabindex="0">
                <Label class="pt-1.5 px-3">{{ $t('When') }}?</Label>
                <div class="px-3 pb-1.5" :class="displayValue ? 'text-custom-gray-900' : 'text-custom-gray-500'">{{ displayValue ? displayValue : $t('Select arrival date') }}</div>
            </div>
        </template>
        <template #content="{ close }">
            <div class="p-4">
                <DatePicker class="nightsSelect" v-model="modelValue" inline multi-calendars auto-apply :formats="{ input: 'dd.MM.yyyy' }" hide-offset-dates :ui="{ dayClass }" :disabled-dates="isDisabled" :time-config="{ enableTimePicker: false }" model-type="yyyy-MM-dd" prevent-min-max-navigation :min-date="new Date()" :max-date="new Date(new Date().setFullYear(new Date().getFullYear() + 1))" @update:model-value="(value) => onDateSelected(value, close)" @invalid-date="onInvalidDate">
                    <template #day="{ day, date }">
                        <span>{{ day }}</span>
                        <template v-if="availableDates.length > 0">
                            <span class="dp--day-status">
                                <Icon v-if="!isDisabled(date)" name="check" size="xs" class="text-green-500" />
                                <span v-else class="dp--day-unavailable">-</span>
                            </span>
                        </template>
                    </template>
                </DatePicker>
                <template v-if="availableDates.length > 0">
                    <DatePickerLegend />
                </template>
            </div>
        </template>
    </Popover>
</template>

<script setup>
import { toIso, findNearestAvailableDate } from '@/utils/dateUtils'
import DatePickerLegend from '@/components/shared/DatePickerLegend.vue'
import { useToast } from '@/composables/toast/useToast.js'
import { useI18n } from 'vue-i18n'

const props = defineProps({
    availableDates: {
        type: Array,
        default: []
    }
})

const emit = defineEmits(['dateSelected'])

const { t, d } = useI18n()
const toast = useToast()

const modelValue = defineModel()

const previousValue = ref(modelValue.value)

watch(modelValue, (value) => {
    previousValue.value = value
})

const formatDate = (iso) => (iso ? iso.split('-').reverse().join('.') : '')

const displayValue = computed(() => {
    if (!modelValue.value) return ''
    if (Array.isArray(modelValue.value)) {
        const [start, end] = modelValue.value
        if (!start) return ''
        return end ? `${formatDate(start)} – ${formatDate(end)}` : formatDate(start)
    }
    return formatDate(modelValue.value)
})

const isDisabled = (date) => {
    if (!Array.isArray(props.availableDates) || props.availableDates.length === 0) {
        return false
    }

    return !props.availableDates.includes(toIso(date))
}

const dayClass = (day) => {
    const iso = toIso(day)
    return props.availableDates.includes(iso) ? 'available-date' : ''
}

const showUnavailableToast = (startDate) => {
    const nearest = findNearestAvailableDate(startDate, props.availableDates)
    toast.warning(t('Arrival on this date is not possible. Nearest possible arrival: :nearest_date', { date: d(nearest, 'full') }))
}

const onDateSelected = (value, close) => {
    if (!value) return

    if (Array.isArray(value)) {
        const [, end] = value
        if (!end) return
    }

    const startDate = Array.isArray(value) ? value[0] : value

    if (Array.isArray(props.availableDates) && props.availableDates.length > 0 && !props.availableDates.includes(startDate)) {
        modelValue.value = previousValue.value
        showUnavailableToast(startDate)
        return
    }

    close()
    emit('dateSelected')
}

/** Fires when the user clicks a disabled (unavailable) calendar day */
const onInvalidDate = (date) => {
    if (!Array.isArray(props.availableDates) || props.availableDates.length === 0) {
        return
    }

    const iso = toIso(date)

    if (!props.availableDates.includes(iso)) {
        showUnavailableToast(iso)
    }
}
</script>
